/**
 * The age-verification step — the second half of the sign-up wizard, also shown
 * to anyone who logs in before clearing the gate. Presentation over `@4f/ui` +
 * the injected {@link SessionStore} (DIP): the 18+ attestation calls
 * `session.verifyAge`, which invokes the edge's age-vendor adapter and returns a
 * fresh session whose user carries `ageOk: true` — the surface gate then lets
 * them through. Blocking the surface here is the compliance red line
 * (`docs/idea/compliance.md`): no explicit content before verification.
 */
import { Button, Checkbox, useT } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { SessionStore } from '../../stores/session.ts';

export interface AgeGateProps {
  readonly session: SessionStore;
}

export function AgeGate(props: AgeGateProps): JSX.Element {
  const { t } = useT();
  const [confirmed, setConfirmed] = createSignal(false);

  const verify = (): void => {
    if (confirmed()) {
      void props.session.verifyAge({ declaredOver18: true });
    }
  };

  return (
    <main class="f4-auth">
      <section class="f4-auth__card" aria-labelledby="age-gate-title">
        <h1 id="age-gate-title" class="f4-auth__title">
          {t('auth.age.title')}
        </h1>
        <p class="f4-auth__subtitle">{t('auth.age.prompt')}</p>

        <div class="f4-auth__form">
          <Checkbox
            id="age-confirm"
            checked={confirmed()}
            onChange={setConfirmed}
            label={t('auth.age.confirm')}
          />

          <Show when={props.session.error()}>
            {(error) => (
              <p class="f4-auth__error" role="alert">
                {error().message}
              </p>
            )}
          </Show>

          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            loading={props.session.loading()}
            loadingLabel={t('auth.loading')}
            disabled={!confirmed()}
            onClick={verify}
          >
            {t('auth.age.submit')}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => props.session.logout()}
          >
            {t('auth.signOut')}
          </Button>
        </div>
      </section>
    </main>
  );
}
